import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, X, Smartphone, Loader2, Check } from 'lucide-react';
import {
  isPushSupported, isIOS, isStandalonePWA,
  shouldShowPushPrompt, dismissPushPrompt,
  subscribeForPush, getCurrentSubscription,
} from '../utils/webPush';

const SHOW_AT_PROGRESS = 25;     // % scroll-through that triggers the prompt
const SHOW_AT_DWELL_MS = 25_000; // OR after this long on the page

/**
 * PushReminderPrompt
 * ─────────────────────────────────────────────────────────────────
 * Slide-up bottom-sheet that appears once mid-article asking the
 * reader if they want a reminder to finish later. Shows up:
 *   • after they scroll past SHOW_AT_PROGRESS, OR
 *   • after SHOW_AT_DWELL_MS on the page
 *   • only if push is supported AND permission is still default
 *   • not for 7 days after they dismiss it
 *
 * On iOS we suppress the prompt unless the page was launched as a
 * standalone PWA — Apple won't even let us request permission outside
 * that mode. Instead we show a one-line "Add to Home Screen for
 * reminders" hint (still dismissible).
 *
 * Props:
 *   targetSelector  — element to scope scroll-progress to (defaults to article).
 *   userId          — optional user id sent with the subscription record.
 */
const PushReminderPrompt = ({ targetSelector = '[data-testid="article-body"]', userId }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle'); // idle | subscribing | subscribed | error
  const [error, setError] = useState('');
  const [hintIos, setHintIos] = useState(false); // iOS in-Safari hint
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    // Native app: <NativePushPrimer> owns the notification moment —
    // never show web-push / Add-to-Home-Screen prompts in the shell.
    if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) return undefined;
    if (!isPushSupported()) return undefined;
    // If the user is already subscribed, never show the prompt.
    let alive = true;
    (async () => {
      const sub = await getCurrentSubscription();
      if (!alive || !mountedRef.current) return;
      if (sub) return; // already subscribed
      if (!shouldShowPushPrompt()) {
        // iOS in-browser specifically: offer a one-line install hint.
        if (isIOS() && !isStandalonePWA() && Notification.permission === 'default') {
          // Respect the 7-day dismissal for this hint too.
          // (shouldShowPushPrompt() already returned false here, so dismissal was set OR iOS test failed)
        }
        return;
      }
      // iOS in regular Safari → show a small "add to home screen" hint instead.
      if (isIOS() && !isStandalonePWA()) {
        setHintIos(true);
        return;
      }

      const start = Date.now();
      const onScrollOrTimer = () => {
        if (!mountedRef.current) return;
        const el = document.querySelector(targetSelector);
        let pct = 0;
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.height > 0) pct = Math.max(0, Math.min(100, ((window.innerHeight - r.top) / r.height) * 100));
        }
        if (pct >= SHOW_AT_PROGRESS || Date.now() - start > SHOW_AT_DWELL_MS) {
          setOpen(true);
          window.removeEventListener('scroll', onScrollOrTimer);
          clearInterval(timer);
        }
      };
      window.addEventListener('scroll', onScrollOrTimer, { passive: true });
      const timer = setInterval(onScrollOrTimer, 2000);
      return () => {
        window.removeEventListener('scroll', onScrollOrTimer);
        clearInterval(timer);
      };
    })();
    return () => { alive = false; };
  }, [targetSelector]);

  const handleAccept = async () => {
    setState('subscribing');
    setError('');
    try {
      await subscribeForPush({ userId });
      if (!mountedRef.current) return;
      setState('subscribed');
      setTimeout(() => mountedRef.current && setOpen(false), 1600);
    } catch (e) {
      const msg = e?.message || 'Could not enable reminders.';
      if (msg === 'IOS_NEEDS_PWA') {
        setHintIos(true);
        setOpen(false);
        return;
      }
      if (msg === 'PERMISSION_DENIED') {
        setError("You've blocked notifications. Enable them in browser settings to turn this on.");
      } else {
        setError(msg);
      }
      setState('error');
    }
  };

  const handleDismiss = () => {
    dismissPushPrompt();
    setOpen(false);
    setHintIos(false);
  };

  // iOS-in-Safari teaser strip
  if (hintIos) {
    const node = (
      <div className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-[360px] z-[9990] animate-slideUp">
        <div className="bg-neutral-950 text-white border border-white/10 rounded-2xl px-4 py-3 shadow-2xl flex items-start gap-3" data-testid="push-prompt-ios-hint">
          <Smartphone className="w-5 h-5 mt-0.5 text-blue-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-snug">Get reminded to finish stories</p>
            <p className="text-xs text-white/70 mt-0.5 leading-snug">
              Tap <b>Share → Add to Home Screen</b>, then open Dreams League from your Home Screen to enable iOS reminders.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-white/60 hover:text-white shrink-0"
            data-testid="push-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <style>{`@keyframes slideUp{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}} .animate-slideUp{animation:slideUp .25s cubic-bezier(.2,.8,.2,1) both}`}</style>
      </div>
    );
    return typeof document === 'undefined' ? null : createPortal(node, document.body);
  }

  if (!open) return null;

  const node = (
    <div className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-[380px] z-[9990] animate-slideUp" data-testid="push-prompt">
      <div className="bg-neutral-950 text-white border border-white/10 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0">
            {state === 'subscribed' ? <Check className="w-5 h-5" /> : <BellRing className="w-5 h-5" />}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold leading-tight">
              {state === 'subscribed' ? "You're all set" : 'Want a nudge to finish this story?'}
            </p>
            <p className="text-xs text-white/70 mt-1 leading-snug">
              {state === 'subscribed'
                ? "We'll ping you with a progress reminder if you wander off mid-read."
                : "We'll send a single notification with your progress so you can pick up right where you left off."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-white/60 hover:text-white shrink-0"
            data-testid="push-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {error && (
          <p className="mt-2 text-[11px] text-red-300/90" data-testid="push-prompt-error">{error}</p>
        )}
        {state !== 'subscribed' && (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleAccept}
              disabled={state === 'subscribing'}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-sm font-bold hover:bg-gray-100 transition active:scale-95 disabled:opacity-70"
              data-testid="push-prompt-accept"
            >
              {state === 'subscribing' ? <><Loader2 className="w-4 h-4 animate-spin" /> Enabling…</> : <><Bell className="w-4 h-4" /> Yes, remind me</>}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="px-3 py-2.5 rounded-full text-xs font-bold text-white/70 hover:text-white"
              data-testid="push-prompt-no"
            >
              No thanks
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}} .animate-slideUp{animation:slideUp .25s cubic-bezier(.2,.8,.2,1) both}`}</style>
    </div>
  );
  return typeof document === 'undefined' ? null : createPortal(node, document.body);
};

export default PushReminderPrompt;
